<template>
	<div>
		<slot name="label">
			<div v-if="label" class="tw-pb-1.5 tw-font-medium tw-text-gray-600">
				<span>{{ label }}</span>
				<span v-if="props.rules.includes('required')" class="tw-text-error-400">*</span>
			</div>
		</slot>
		<div
			class="input-wrapper tw-flex tw-w-full tw-items-stretch tw-rounded-lg tw-border tw-transition-all"
			:class="computedClasses"
		>
			<div
				class="tw-relative tw-flex tw-max-w-full tw-flex-1 tw-items-center tw-gap-2 tw-rounded-lg tw-bg-white"
				:class="sizeClasses[props.size]"
			>
				<slot name="start-icon"></slot>
				<slot name="start-badge">
					<div
						v-if="startBadge"
						class="tw-rounded-md tw-bg-gray-100 tw-p-1.5 tw-text-sm tw-font-medium tw-text-gray-600"
					>
						{{ startBadge }}
					</div>
				</slot>
				<input
					class="tw-w-full tw-bg-white tw-text-base !tw-placeholder-gray-500 tw-outline-none disabled:tw-cursor-not-allowed"
					:class="dirClass"
					v-model="inputModel"
					:placeholder="placeholder"
					:type="type"
					:disabled="disabled"
					v-bind="$attrs"
					ref="inputElementRef"
					:style="size == 'xs' ? 'height: 26px' : 'height: 24px'"
					:list="autoCompleteListId"
				/>
				<datalist :id="autoCompleteListId">
					<option
						v-for="(value, index) in autoCompleteOptions"
						:key="index"
						:value="value"
					></option>
				</datalist>
				<Spinner v-if="loading" size="20px" class="spinner" />
				<slot name="end-badge">
					<div
						v-if="endBadge"
						class="tw-rounded-md tw-bg-gray-100 tw-p-1.5 tw-text-sm tw-font-medium tw-text-gray-600"
					>
						{{ endBadge }}
					</div>
				</slot>
				<InlineSvg
					v-if="hasErrorMessage"
					class="tw-text-error-500"
					:src="loadFile('assets/v2/image/icons/alert-circle.svg')"
				/>
				<slot v-else name="end-icon"></slot>
			</div>
			<div v-if="hasEndButtonSlot" class="tw-w-px tw-bg-gray-300"></div>
			<div v-if="hasEndButtonSlot">
				<div class="tw-w-max">
					<slot name="end-btn"> </slot>
				</div>
			</div>
		</div>
		<div
			:class="rules.length || hint || allocateBottomSpace ? 'tw-pt-1 tw-text-sm' : ''"
			:style="rules.length || hint || allocateBottomSpace ? 'min-height: 20px' : ''"
		>
			<div v-if="!errorMessage" class="tw-text-gray-600">
				<slot name="hint">
					{{ hint }}
				</slot>
			</div>
			<div v-if="errorMessage" class="tw-text-error-500">
				{{ errorMessage }}
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, ref, useSlots } from 'vue';
import { debounce as debounceFunc } from 'lodash';
import InlineSvg from '../inline-svg';
import { loadFile } from '@/v2/utils/index';
import { useFieldValidation } from '@/v2/utils/forms';
import Spinner from '../spinner.vue';
import { useLayoutStore } from '@/v2/store/shared/layout-store';

const layoutStore = useLayoutStore();

const props = defineProps({
	modelValue: {},
	label: {
		type: String,
		default: '',
	},
	placeholder: {
		type: String,
		default: '',
	},
	hint: {
		type: String,
		default: '',
	},
	size: {
		type: String,
		default: 'md',
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	rules: {
		type: Array,
		default: () => [],
	},
	debounce: {
		type: Number,
		default: 0,
	},
	type: {
		type: String,
		default: 'text',
	},
	allocateBottomSpace: {
		type: Boolean,
		default: false,
	},
	forceNumericDirection: {
		type: Boolean,
		default: true,
	},
	startBadge: {
		type: String,
		default: '',
	},
	endBadge: {
		type: String,
		default: '',
	},
	autoCompleteOptions: {
		type: Array,
		default: () => [],
	},
	inputClasses: {
		type: String,
		default: '',
	},
});

const emit = defineEmits(['update:modelValue']);
const slots = useSlots();
const inputElementRef = ref(null);
const autoCompleteListId = computed(
	() => `autocomplete-${Math.random().toString(36).substring(2)}`,
);

const hasEndButtonSlot = computed(() => {
	return slots['end-btn'];
});

const emitModel = (val) => {
	if (props.type == 'number' && val !== '' && val !== null && val !== undefined)
		emit('update:modelValue', +val);
	else emit('update:modelValue', val);
};
const inputModel = computed({
	get() {
		return props.modelValue;
	},
	set: props.debounce ? debounceFunc(emitModel, props.debounce) : emitModel,
});

const dirClass = computed(() => {
	return props.forceNumericDirection
		? layoutStore.isRTL && props.type === 'number'
			? 'dir-rtl'
			: ''
		: '';
});

const { errorMessage, hasErrorMessage, resetErrorMessage } = useFieldValidation(
	() => props.modelValue,
	() => props.rules,
);

const sizeClasses = {
	xs: 'tw-px-2 tw-py-0.5',
	sm: 'tw-px-3 tw-py-2',
	md: 'tw-px-3.5 tw-py-2.5',
};
const computedClasses = computed(() => {
	const res = [];
	if (hasErrorMessage.value) {
		res.push(
			'tw-border-error-300 focus-within:tw-border-error-300 focus-within:tw-ring focus-within:tw-ring-error-500/[.24]',
		);
	} else {
		res.push('tw-border-gray-300');
	}
	if (!hasErrorMessage.value && !props.disabled) {
		res.push('focus-within:tw-border-brand-300');
	}
	if (props.disabled) {
		res.push('tw-text-gray-500');
	}
	if (props.inputClasses) {
		res.push(props.inputClasses);
	}
	return res;
});

const focus = () => {
	inputElementRef.value?.focus?.();
};

defineExpose({
	focus,
	hasErrorMessage,
	resetErrorMessage,
});
</script>

<style lang="scss" scoped>
::v-deep(.lds-ring div) {
	margin: auto;
}
</style>

<style lang="scss" scoped>
@import 'tailwindcss/base';
@import 'tailwindcss/utilities';
.dir-rtl {
	direction: rtl !important;
}
.dir-ltr {
	direction: ltr !important;
}
</style>
